import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { TosVersion_Entity } from "../../entities/tosVersion";
import { rhapsodyApi } from "../api/rhapsodyApi";

const tosVersionsAdapter = createEntityAdapter<TosVersion_Entity>();
const initialState = tosVersionsAdapter.getInitialState();

/**
 * Terms of Service versions.
 *
 * There is no delete mutation, and rhapsody-service has no delete route either.
 * A published version is what some organizations have already acknowledged;
 * removing the row would orphan their acknowledgements and destroy the evidence
 * that they agreed to anything. Retire a version by clearing `active`.
 */
export const tosVersionEndpoints = rhapsodyApi.injectEndpoints({
  endpoints: (build) => ({
    getTosVersions: build.query<EntityState<TosVersion_Entity>, void>({
      query: () => `tos/versions`,
      transformResponse: (responseData: TosVersion_Entity[]) => {
        return tosVersionsAdapter.setAll(initialState, responseData);
      },
      providesTags: ["tosVersions"],
    }),
    createTosVersion: build.mutation<
      TosVersion_Entity,
      Partial<TosVersion_Entity>
    >({
      query: (body) => ({
        method: "POST",
        body,
        url: `tos/versions`,
      }),
      invalidatesTags: ["tosVersions"],
    }),
    updateTosVersion: build.mutation<
      TosVersion_Entity,
      { id: number; body: Partial<TosVersion_Entity> }
    >({
      query: (args) => ({
        method: "PUT",
        body: args.body,
        url: `tos/versions/${args.id}`,
      }),
      invalidatesTags: ["tosVersions"],
    }),
  }),
});

export const {
  useGetTosVersionsQuery,
  useCreateTosVersionMutation,
  useUpdateTosVersionMutation,
} = tosVersionEndpoints;
